/**
 * "How AdmissionCrafts works" — three-step block.
 *
 * Feb 2026 update: the local "Tap to translate" language toggle has been
 * removed per product decision. The section now respects whatever language
 * the user picked in the top nav (via i18n). One source of truth for locale
 * across the site.
 */
import { useTranslation } from "react-i18next";
import { ShieldCheck } from "lucide-react";

const STEPS = ["s1", "s2", "s3"];

export default function HowItWorks() {
  const { t } = useTranslation();

  return (
    <section
      id="how-it-works"
      className="bg-white border-b border-slate-200 scroll-mt-20"
      data-testid="section-how-it-works"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
        <div className="max-w-2xl">
          <p className="text-[11px] tracking-[0.2em] uppercase font-semibold text-amber-700">
            {t("how.eyebrow")}
          </p>
          <h2 className="mt-3 font-display font-bold text-slate-900 tracking-tight text-3xl sm:text-4xl leading-[1.1]">
            {t("how.title")}
          </h2>
          <p className="mt-4 text-base text-slate-600 leading-relaxed">
            {t("how.subtitle")}
          </p>
        </div>

        <ol className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8" data-testid="how-steps">
          {STEPS.map((s, i) => (
            <li
              key={s}
              className="border border-slate-200 rounded-xl p-6 sm:p-7 bg-white animate-fade-in"
              data-testid={`how-step-${i + 1}`}
            >
              <div className="font-display tabular-nums text-amber-700 text-3xl font-bold">
                0{i + 1}
              </div>
              <h3 className="mt-3 font-display text-lg font-bold text-slate-900">
                {t(`how.${s}Title`)}
              </h3>
              <p className="mt-2 text-[14px] text-slate-600 leading-relaxed">
                {t(`how.${s}Body`)}
              </p>
            </li>
          ))}
        </ol>

        <div className="mt-10 flex items-start gap-2 text-xs text-slate-500">
          <ShieldCheck className="w-4 h-4 mt-0.5 text-emerald-600 flex-shrink-0" />
          <span>{t("how.trustNote")}</span>
        </div>
      </div>
    </section>
  );
}
